import { useState } from 'react';

import { Credentials } from '../components/Credentials';
import { Card, Empty, SortHeader, StatTile } from '../components/ui';
import { api } from '../lib/api';
import { ago } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useSort } from '../lib/useSort';

const ROLES = ['SUPERADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY', 'CLIENT_VIEWER'] as const;
type Role = (typeof ROLES)[number];

/** What each role can actually do, in words, next to the control that grants it. */
const ROLE_HELP: Record<Role, string> = {
  SUPERADMIN: 'Everything, including editing a deployment’s .env and restarting deployments',
  ADMIN: 'Manage users, edit servers and costs, trigger a collection',
  OPERATOR: 'Edit servers and costs. No user management',
  READ_ONLY: 'View everything. Change nothing',
  CLIENT_VIEWER: 'View one client’s estate only',
};

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: boolean;
  hasPassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  isSelf: boolean;
  client: { code: string; name: string } | null;
}

interface AuditRow {
  id: string;
  userEmail: string | null;
  action: string;
  entityType: string;
  detail: string | null;
  createdAt: string;
}

export function Admin() {
  const { data, error, loading, reload } = useApi<UserRow[]>('/users', 0);
  const { data: audit, reload: reloadAudit } = useApi<AuditRow[]>('/users/audit', 0);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('READ_ONLY');

  const act = async (id: string, fn: () => Promise<unknown>, note: string) => {
    setBusy(id);
    setFailure(null);
    setMessage(null);
    try {
      await fn();
      setMessage(note);
      reload();
      reloadAudit();
    } catch (e) {
      // The API refuses self-demotion, last-superadmin removal and privilege
      // escalation with specific messages. Showing them verbatim is more useful
      // than a generic failure.
      setFailure(e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(null);
      setConfirmDelete(null);
    }
  };

  const { sorted, state: sort } = useSort<UserRow>(
    data ?? [],
    {
      email: (u) => u.email,
      name: (u) => u.name,
      role: (u) => ROLES.indexOf(u.role),
      status: (u) => (u.isActive ? 'active' : 'disabled'),
      signin: (u) => u.lastLoginAt,
    },
    'role',
  );

  if (loading && !data) return <p className="empty">Loading users…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;

  const users = data ?? [];

  return (
    <>
      <div className="page-head">
        <h1>Administration</h1>
      </div>

      <div className="tiles">
        <StatTile label="Accounts" value={users.length} />
        <StatTile label="Superadmins" value={users.filter((u) => u.role === 'SUPERADMIN' && u.isActive).length} />
        <StatTile
          label="Disabled"
          value={users.filter((u) => !u.isActive).length}
          tone={users.filter((u) => !u.isActive).length > 0 ? 'warn' : undefined}
        />
        <StatTile
          label="Password logins"
          value={users.filter((u) => u.hasPassword).length}
          sub={users.filter((u) => u.hasPassword).length === 0 ? 'SSO is the only way in' : 'break-glass access'}
          tone={users.filter((u) => u.hasPassword).length === 0 ? 'warn' : undefined}
        />
      </div>

      {message && <p className="notice">{message}</p>}
      {failure && <p className="notice notice-bad" role="alert">{failure}</p>}

      <Card title="Accounts">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader label="Email" sortKey="email" state={sort} />
                <SortHeader label="Name" sortKey="name" state={sort} />
                <SortHeader label="Role" sortKey="role" state={sort} />
                <SortHeader label="Status" sortKey="status" state={sort} />
                <SortHeader label="Last sign-in" sortKey="signin" state={sort} />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => (
                <tr key={u.id} className={u.isActive ? undefined : 'row-disabled'}>
                  <td>
                    {u.email}
                    {u.isSelf && <span className="tag">you</span>}
                    {!u.hasPassword && (
                      <span className="tag" title="No password set — this account can only sign in with Microsoft">
                        SSO
                      </span>
                    )}
                  </td>
                  <td>{u.name}</td>
                  <td>
                    <select
                      value={u.role}
                      disabled={busy === u.id || u.isSelf}
                      title={u.isSelf ? 'You cannot change your own role' : ROLE_HELP[u.role]}
                      onChange={(e) =>
                        void act(u.id, () =>
                          api(`/users/${u.id}/role`, {
                            method: 'PATCH',
                            body: JSON.stringify({ role: e.target.value }),
                          }),
                        `${u.email} is now ${e.target.value}`)
                      }
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`pill ${u.isActive ? 'pill-up' : 'pill-down'}`}>
                      <span aria-hidden="true">{u.isActive ? '●' : '■'}</span>
                      {u.isActive ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td title={u.lastLoginAt ?? ''}>
                    {u.lastLoginAt ? ago(u.lastLoginAt) : <span className="muted">never</span>}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="ghost"
                        disabled={busy === u.id || u.isSelf}
                        title={u.isSelf ? 'You cannot disable your own account' : undefined}
                        onClick={() =>
                          void act(u.id, () =>
                            api(`/users/${u.id}/active`, {
                              method: 'PATCH',
                              body: JSON.stringify({ isActive: !u.isActive }),
                            }),
                          `${u.email} ${u.isActive ? 'disabled' : 'enabled'}`)
                        }
                      >
                        {u.isActive ? 'Disable' : 'Enable'}
                      </button>

                      {confirmDelete === u.id ? (
                        <>
                          <button type="button" disabled={busy === u.id}
                            onClick={() => void act(u.id, () => api(`/users/${u.id}`, { method: 'DELETE' }), `${u.email} deleted`)}>
                            Really delete
                          </button>
                          <button type="button" className="ghost" onClick={() => setConfirmDelete(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" className="ghost" disabled={busy === u.id || u.isSelf}
                          onClick={() => setConfirmDelete(u.id)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {users.length === 0 && <Empty>No accounts.</Empty>}

        <dl className="facts role-help">
          {ROLES.map((r) => (
            <div key={r}>
              <dt>{r}</dt>
              <dd>{ROLE_HELP[r]}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card title="Add an account">
        <p className="muted small">
          {/* Sign-in creates the account anyway; this exists so a role can be set
              before someone's first visit rather than after. */}
          Anyone with an <code>@adeptiviq.com</code> account can sign in with Microsoft and is
          created as READ_ONLY automatically. Add them here to set their role in advance.
        </p>
        <div className="cost-form">
          <label>
            Email
            <input type="email" value={inviteEmail} placeholder="first.last@adeptiviq.com"
              onChange={(e) => setInviteEmail(e.target.value)} />
          </label>
          <label>
            Name
            <input type="text" value={inviteName} placeholder="First Last"
              onChange={(e) => setInviteName(e.target.value)} />
          </label>
          <label>
            Role
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
        </div>
        <div className="cost-actions">
          <button
            type="button"
            disabled={busy === 'invite' || !inviteEmail.trim()}
            onClick={() =>
              void act('invite', () =>
                api('/users', {
                  method: 'POST',
                  body: JSON.stringify({ email: inviteEmail.trim(), name: inviteName.trim(), role: inviteRole }),
                }).then(() => {
                  setInviteEmail('');
                  setInviteName('');
                }),
              `${inviteEmail.trim()} added as ${inviteRole}`)
            }
          >
            Add account
          </button>
        </div>
      </Card>

      {/* Renders nothing for an ADMIN: the API refuses, and a section that only
          ever errors is worse than one that is not there. */}
      <Credentials />

      <Card title="Recent administrative activity">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th>When</th><th>Who</th><th>Action</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {(audit ?? []).map((a) => (
                <tr key={a.id}>
                  <td title={a.createdAt}>{ago(a.createdAt)}</td>
                  <td>{a.userEmail ?? <span className="muted">system</span>}</td>
                  <td className="mono small">{a.action}</td>
                  <td className="small">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(audit ?? []).length === 0 && <Empty>Nothing recorded yet.</Empty>}
      </Card>
    </>
  );
}
